import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { Integration, IntegrationLog } from '../types';

const PROVIDERS = [
  {
    key: 'amazon_ses',
    label: 'Amazon SES',
    icon: 'ri-amazon-line',
    accent: '#FF9900',
    description: 'Simple Email Service da AWS — alta entregabilidade e escala',
  },
  {
    key: 'smtp',
    label: 'SMTP Genérico',
    icon: 'ri-mail-settings-line',
    accent: '#6366f1',
    description: 'Qualquer servidor SMTP — Postmark, SendGrid, Mailgun etc.',
  },
];

const STATUS_META = {
  connected: { label: 'Conectado', color: 'bg-emerald-500/20 text-emerald-400', icon: 'ri-checkbox-circle-line' },
  error:     { label: 'Erro',      color: 'bg-red-500/20 text-red-400',         icon: 'ri-error-warning-line' },
  untested:  { label: 'Não testado', color: 'bg-white/10 text-white/40',        icon: 'ri-question-line' },
};

interface Props {
  integrations: Integration[];
  onSave: (id: string, data: Partial<Integration>) => Promise<void>;
  onTest: (id: string, type: string) => Promise<{ ok: boolean; message: string }>;
  logs: IntegrationLog[];
}

export const EmailIntegration = ({ integrations, onSave, onTest, logs }: Props) => {
  const [activeProvider, setActiveProvider] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  const current = integrations.find((i) => i.provider === activeProvider);
  const currentLogs = logs.filter((l) => l.integration_id === current?.id).slice(0, 10);

  useEffect(() => {
    if (current) setEditing({ ...current.config_json });
  }, [activeProvider, current?.id]);

  const handleSave = async () => {
    if (!current) return;
    setSaving(true);
    // Normalize all string fields before saving
    const normalized: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(editing)) {
      normalized[k] = typeof v === 'string' ? v.trim() : v;
    }
    // Ensure port is a number
    if (normalized.port !== undefined) {
      normalized.port = Number(normalized.port) || 587;
    }
    await onSave(current.id, { config_json: normalized });
    setSaving(false);
  };

  const handleTest = async () => {
    if (!current || !testEmail.trim()) return;
    setTesting(true);
    setTestResult(null);
    // Save current editing state first so the edge function gets fresh data
    const normalized: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(editing)) {
      normalized[k] = typeof v === 'string' ? v.trim() : v;
    }
    if (normalized.port !== undefined) normalized.port = Number(normalized.port) || 587;
    await onSave(current.id, { config_json: normalized });
    const result = await onTest(current.id, testEmail.trim());
    setTestResult(result);
    setTesting(false);
  };

  // Real-time SMTP validation helper
  const getSmtpValidationErrors = (): string[] => {
    if (activeProvider !== 'smtp') return [];
    const errors: string[] = [];
    if (!String(editing.host || '').trim()) errors.push('Host obrigatório');
    if (!Number(editing.port)) errors.push('Porta obrigatória');
    if (!String(editing.username || '').trim()) errors.push('Usuário obrigatório');
    if (!String(editing.password || '').trim()) errors.push('Senha / Token obrigatório');
    if (!String(editing.sender_email || '').trim()) errors.push('E-mail do Remetente obrigatório');
    if (!String(editing.sender_name || '').trim()) errors.push('Nome do Remetente obrigatório');
    const port = Number(editing.port);
    const enc = String(editing.encryption || 'tls');
    if (port === 465 && enc !== 'ssl') errors.push('Porta 465 requer SSL');
    if (port === 587 && enc === 'ssl') errors.push('Porta 587 usa TLS, não SSL');
    return errors;
  };

  const toggle = (field: string) => setShowSecrets((p) => ({ ...p, [field]: !p[field] }));

  const providerIntegration = (key: string) => integrations.find((i) => i.provider === key);
  const statusMeta = (status: string) => STATUS_META[status as keyof typeof STATUS_META] || STATUS_META.untested;

  return (
    <div className="space-y-6">
      {/* Provider cards */}
      <div className="grid grid-cols-2 gap-4">
        {PROVIDERS.map((p) => {
          const integ = providerIntegration(p.key);
          const sm = statusMeta(integ?.last_status || 'untested');
          return (
            <button
              key={p.key}
              onClick={() => setActiveProvider(activeProvider === p.key ? null : p.key)}
              className={`text-left border rounded-2xl p-5 transition-all cursor-pointer ${
                activeProvider === p.key ? 'border-violet-500/50' : 'border-white/10 hover:border-white/20'
              }`}
              style={{ background: activeProvider === p.key ? `${p.accent}12` : `${p.accent}08` }}
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl"
                  style={{ background: `${p.accent}20` }}
                >
                  <i className={`${p.icon} text-[22px]`} style={{ color: p.accent }} />
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <span className={`text-[11px] font-700 px-2.5 py-1 rounded-full flex items-center gap-1 ${sm.color}`}>
                    <i className={`${sm.icon} text-[12px]`} />
                    {sm.label}
                  </span>
                  {integ?.is_active && (
                    <span className="text-[10px] font-700 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">Ativo</span>
                  )}
                </div>
              </div>
              <h3 className="text-[15px] font-700 text-white mb-1">{p.label}</h3>
              <p className="text-[12px] text-white/40 leading-relaxed">{p.description}</p>
              {integ?.last_tested_at && (
                <p className="text-[11px] text-white/25 mt-3">
                  Testado em {new Date(integ.last_tested_at).toLocaleString('pt-BR')}
                </p>
              )}
            </button>
          );
        })}
      </div>

      {/* Config panel */}
      {activeProvider && current && (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <i className={`${PROVIDERS.find((p) => p.key === activeProvider)?.icon} text-[18px] text-white/60`} />
              <h2 className="text-[15px] font-700 text-white">
                Configurar {PROVIDERS.find((p) => p.key === activeProvider)?.label}
              </h2>
            </div>
            <button
              onClick={() => setActiveProvider(null)}
              className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-white/60" />
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-8">
              {/* LEFT: credentials */}
              <div className="space-y-5">
                <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider">Remetente</p>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Nome do Remetente</label>
                  <input
                    type="text"
                    value={String(editing.sender_name || '')}
                    onChange={(e) => setEditing({ ...editing, sender_name: e.target.value })}
                    placeholder="Ex: Suporte PandaPro"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20"
                  />
                </div>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">E-mail do Remetente</label>
                  <input
                    type="email"
                    value={String(editing.sender_email || '')}
                    onChange={(e) => setEditing({ ...editing, sender_email: e.target.value })}
                    placeholder="noreply@seudominio.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20"
                  />
                </div>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">E-mail de Resposta (Reply-To)</label>
                  <input
                    type="email"
                    value={String(editing.reply_to || '')}
                    onChange={(e) => setEditing({ ...editing, reply_to: e.target.value })}
                    placeholder="suporte@seudominio.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20"
                  />
                </div>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Domínio de Envio</label>
                  <input
                    type="text"
                    value={String(editing.domain || '')}
                    onChange={(e) => setEditing({ ...editing, domain: e.target.value })}
                    placeholder="seudominio.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20"
                  />
                </div>

                <div className="pt-2">
                  <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider mb-4">Credenciais</p>

                  {activeProvider === 'amazon_ses' && (
                    <>
                      <div className="mb-4">
                        <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Região AWS</label>
                        <select
                          value={String(editing.region || 'us-east-1')}
                          onChange={(e) => setEditing({ ...editing, region: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 cursor-pointer"
                        >
                          {['us-east-1', 'us-west-2', 'eu-west-1', 'ap-southeast-1', 'sa-east-1'].map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </div>
                      <div className="mb-4">
                        <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Access Key ID</label>
                        <input
                          type="text"
                          value={String(editing.access_key_id || '')}
                          onChange={(e) => setEditing({ ...editing, access_key_id: e.target.value })}
                          placeholder="AKIA..."
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20"
                        />
                      </div>
                      <div className="relative">
                        <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Secret Access Key</label>
                        <input
                          type={showSecrets.secret ? 'text' : 'password'}
                          value={String(editing.secret_access_key || '')}
                          onChange={(e) => setEditing({ ...editing, secret_access_key: e.target.value })}
                          placeholder="••••••••"
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 pr-10"
                        />
                        <button onClick={() => toggle('secret')} className="absolute right-3 top-[calc(50%+8px)] -translate-y-1/2 cursor-pointer" type="button">
                          <i className={`${showSecrets.secret ? 'ri-eye-off-line' : 'ri-eye-line'} text-white/30 text-[14px]`} />
                        </button>
                      </div>
                    </>
                  )}

                  {activeProvider === 'smtp' && (
                    <>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                          <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Host</label>
                          <input
                            type="text"
                            value={String(editing.host || '')}
                            onChange={(e) => setEditing({ ...editing, host: e.target.value })}
                            placeholder="smtp.sendgrid.net"
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Porta</label>
                          <input
                            type="number"
                            value={Number(editing.port || 587)}
                            onChange={(e) => setEditing({ ...editing, port: parseInt(e.target.value, 10) })}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500"
                          />
                        </div>
                      </div>
                      <div className="mb-4">
                        <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Criptografia</label>
                        <div className="flex gap-2">
                          {(['tls', 'ssl', 'none'] as const).map((enc) => (
                            <button
                              key={enc}
                              onClick={() => setEditing({ ...editing, encryption: enc })}
                              className={`flex-1 py-2 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${editing.encryption === enc ? 'bg-violet-600 text-white' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                            >
                              {enc.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="mb-4">
                        <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Usuário</label>
                        <input
                          type="text"
                          value={String(editing.username || '')}
                          onChange={(e) => setEditing({ ...editing, username: e.target.value })}
                          placeholder="apikey ou usuario"
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20"
                        />
                      </div>
                      <div className="relative">
                        <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Senha / Token</label>
                        <input
                          type={showSecrets.smtp_pass ? 'text' : 'password'}
                          value={String(editing.password || '')}
                          onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                          placeholder="••••••••"
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 pr-10"
                        />
                        <button onClick={() => toggle('smtp_pass')} className="absolute right-3 top-[calc(50%+8px)] -translate-y-1/2 cursor-pointer" type="button">
                          <i className={`${showSecrets.smtp_pass ? 'ri-eye-off-line' : 'ri-eye-line'} text-white/30 text-[14px]`} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* RIGHT: settings + status */}
              <div className="space-y-5">
                <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider">Configurações</p>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Ambiente</label>
                  <div className="flex gap-2">
                    {(['sandbox', 'production'] as const).map((env) => (
                      <button
                        key={env}
                        onClick={async () => await onSave(current.id, { environment: env })}
                        className={`flex-1 py-2.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${current.environment === env ? env === 'production' ? 'bg-emerald-600 text-white' : 'bg-yellow-600 text-white' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                      >
                        {env === 'sandbox' ? 'Teste' : 'Produção'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-white/5">
                  <div>
                    <p className="text-[13px] text-white font-600">Provedor ativo</p>
                    <p className="text-[11px] text-white/30">Usar este provedor para envios do sistema</p>
                  </div>
                  <button
                    onClick={async () => await onSave(current.id, { is_active: !current.is_active })}
                    className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative flex-shrink-0 overflow-hidden ${current.is_active ? 'bg-violet-600' : 'bg-white/10'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${current.is_active ? 'left-6' : 'left-1'}`} />
                  </button>
                </div>

                {/* Status card */}
                <div className={`rounded-xl p-4 border ${current.last_status === 'connected' ? 'bg-emerald-500/10 border-emerald-500/20' : current.last_status === 'error' ? 'bg-red-500/10 border-red-500/20' : 'bg-white/5 border-white/10'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <i className={`${statusMeta(current.last_status).icon} text-[16px] ${statusMeta(current.last_status).color.split(' ')[1]}`} />
                    <span className="text-[13px] font-700 text-white">Status da Conexão</span>
                  </div>
                  <p className="text-[12px] text-white/50">
                    {current.last_status === 'connected' && 'Conexão verificada com sucesso'}
                    {current.last_status === 'error' && (current.last_error || 'Erro ao conectar')}
                    {current.last_status === 'untested' && 'Ainda não testado — envie um e-mail de teste para verificar'}
                  </p>
                  {current.last_tested_at && (
                    <p className="text-[11px] text-white/25 mt-2">
                      Último teste: {new Date(current.last_tested_at).toLocaleString('pt-BR')}
                    </p>
                  )}
                </div>

                {/* Test button */}
                <button
                  onClick={() => setShowTestModal(true)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/70 text-[13px] font-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-send-plane-line text-[14px]" />
                  Testar Envio de E-mail
                </button>

                {/* Recent logs */}
                {currentLogs.length > 0 && (
                  <div>
                    <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider mb-3">Últimos Envios</p>
                    <div className="space-y-2">
                      {currentLogs.map((log) => (
                        <div key={log.id} className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2">
                          <div className="flex items-center gap-2">
                            <i className={`text-[12px] ${log.status === 'success' ? 'ri-checkbox-circle-line text-emerald-400' : 'ri-close-circle-line text-red-400'}`} />
                            <span className="text-[12px] text-white/60">{log.event_type}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-white/30">{new Date(log.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* SMTP real-time validation panel */}
            {activeProvider === 'smtp' && (() => {
              const errs = getSmtpValidationErrors();
              if (errs.length === 0) return (
                <div className="mt-4 flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3">
                  <i className="ri-checkbox-circle-line text-emerald-400 text-[16px]" />
                  <span className="text-[12px] text-emerald-300 font-600">Configuração válida — pronto para salvar e testar</span>
                </div>
              );
              return (
                <div className="mt-4 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-alert-line text-amber-400 text-[14px]" />
                    <span className="text-[12px] text-amber-300 font-600">Preencha os campos obrigatórios:</span>
                  </div>
                  <ul className="space-y-1">
                    {errs.map((e) => (
                      <li key={e} className="text-[11px] text-amber-200/70 flex items-center gap-1.5">
                        <i className="ri-arrow-right-s-line text-amber-400" />
                        {e}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-white/10">
              <button onClick={() => setActiveProvider(null)} className="px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/60 text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                {saving ? 'Salvando...' : 'Salvar Configurações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test modal */}
      {showTestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-[15px] font-700 text-white">Testar Envio</h3>
              <button onClick={() => { setShowTestModal(false); setTestResult(null); }} className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer">
                <i className="ri-close-line text-white/60" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">E-mail de destino para teste</label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20"
                />
              </div>
              {testResult && (
                <div className={`rounded-xl p-3 flex items-start gap-2 ${testResult.ok ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                  <i className={`text-[14px] mt-0.5 ${testResult.ok ? 'ri-checkbox-circle-line text-emerald-400' : 'ri-error-warning-line text-red-400'}`} />
                  <p className="text-[12px] text-white/70">{testResult.message}</p>
                </div>
              )}
              <button
                onClick={handleTest}
                disabled={testing || !testEmail.trim()}
                className="w-full py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                {testing ? 'Enviando...' : 'Enviar E-mail de Teste'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
